import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useShop } from '../context/ShopContext';
import { 
  CheckCircle, 
  ShieldCheck, 
  CreditCard, 
  QrCode, 
  Building2, 
  Truck, 
  Sparkles, 
  Check, 
  ArrowLeft,
  Lock,
  ArrowRight,
  FileText,
  Tag,
  Copy
} from 'lucide-react';
import { formatINR } from '../data/products';
import confetti from 'canvas-confetti';

export default function CheckoutPage() {
  const navigate = useNavigate();
  const {
    cart,
    cartCount,
    cartSubtotal,
    discountAmount,
    gstAmount,
    cartTotal,
    shippingFee,
    clearCart,
    addNewOrder,
    addToast,
    masterMetalRates,
    calculateProductPrice,
    storeSettings,
    currentCustomer,
    isCustomerLoggedIn,
    openCustomerAuthModal,
    couponCode,
    couponMessage,
    appliedDiscountPercent,
    applyCoupon,
    removeCoupon
  } = useShop();

  const [formData, setFormData] = useState({
    fullName: currentCustomer?.name || '',
    phone: currentCustomer?.phone || '',
    email: currentCustomer?.email || '',
    address: currentCustomer?.address || '',
    city: currentCustomer?.city || 'Jaipur',
    state: 'Rajasthan',
    pincode: '302002'
  });

  // Sync if customer logs in while on checkout
  React.useEffect(() => {
    if (currentCustomer) {
      setFormData(prev => ({
        ...prev,
        fullName: prev.fullName || currentCustomer.name || '',
        phone: prev.phone || currentCustomer.phone || '',
        email: prev.email || currentCustomer.email || '',
        address: prev.address || currentCustomer.address || '',
        city: prev.city || currentCustomer.city || 'Jaipur'
      }));
    }
  }, [currentCustomer]);

  const [paymentMethod, setPaymentMethod] = useState('razorpay');
  const [isProcessingRzp, setIsProcessingRzp] = useState(false);
  const [paidPaymentId, setPaidPaymentId] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [orderId, setOrderId] = useState('');
  const [inputCoupon, setInputCoupon] = useState('');
  const [upiUtr, setUpiUtr] = useState('');
  const [copiedUpi, setCopiedUpi] = useState(false);

  const handleApplyCoupon = (e) => {
    if (e) e.preventDefault();
    if (!inputCoupon.trim()) return;
    applyCoupon(inputCoupon);
    setInputCoupon('');
  };

  const copyUpiId = () => {
    const upi = storeSettings?.upiId || 'mljewellers@icici';
    navigator.clipboard?.writeText(upi);
    setCopiedUpi(true);
    addToast('UPI ID copied to clipboard!', 'success');
    setTimeout(() => setCopiedUpi(false), 2500);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const finalizeOrder = (methodName, paymentId = '') => {
    try {
      confetti({
        particleCount: 140,
        spread: 80,
        origin: { y: 0.6 },
        colors: ['#d4af37', '#faeb92', '#10b981', '#ffffff']
      });
    } catch (err) {
      console.log(err);
    }

    const generatedId = `MLJ-${Math.floor(100000 + Math.random() * 900000)}`;
    setOrderId(generatedId);
    if (paymentId) setPaidPaymentId(paymentId);

    if (addNewOrder) {
      addNewOrder({
        id: generatedId,
        date: 'Just Now',
        customer: {
          name: formData.fullName,
          phone: formData.phone,
          email: formData.email,
          address: `${formData.address}, ${formData.city}, ${formData.state} - ${formData.pincode}`
        },
        items: cart.map(c => {
          const product = c?.product || c;
          const livePricing = calculateProductPrice ? calculateProductPrice(product, masterMetalRates) : null;
          const unitPrice = livePricing?.finalPrice || Number(product?.price) || 0;
          return {
            name: product?.name || 'Jewellery Item',
            qty: Number(c?.quantity) || 1,
            price: unitPrice,
            metal: product?.metal || '22K Gold'
          };
        }),
        paymentMethod: paymentId 
          ? `Razorpay Online (${paymentId})` 
          : (paymentMethod === 'upi' ? `Showroom UPI QR ${upiUtr ? `(UTR: ${upiUtr})` : ''}` : methodName),
        total: cartTotal,
        status: paymentId ? 'Paid / Confirmed' : (paymentMethod === 'upi' ? 'UPI Verification Pending' : 'Processing'),
        notes: paymentId ? `Razorpay Payment ID: ${paymentId}` : (upiUtr ? `UPI Transaction Reference: ${upiUtr}` : '')
      });
    }

    setIsSubmitted(true);
    setIsProcessingRzp(false);
    clearCart();
    addToast(paymentId ? `Payment of ${formatINR(cartTotal)} successful! Order #${generatedId} confirmed.` : (paymentMethod === 'upi' ? 'Order placed via Showroom UPI QR! Recorded in Admin Orders.' : 'Order placed successfully! Recorded in Admin Orders.'), 'success');
  };

  const handlePlaceOrder = (e) => {
    e.preventDefault();

    if (!formData.fullName.trim() || !formData.phone.trim() || !formData.address.trim()) {
      addToast('Please fill in your name, phone number, and delivery address.', 'error');
      return;
    }

    // 1. RAZORPAY PAYMENT FLOW
    if (paymentMethod === 'razorpay') {
      setIsProcessingRzp(true);

      const rzpKey = storeSettings?.razorpayKeyId || 'rzp_test_TkcMWJI1AFGNfu';

      const launchRazorpay = () => {
        if (!window.Razorpay) {
          setIsProcessingRzp(false);
          addToast('Razorpay SDK could not be loaded. Please check your internet connection or choose another payment method.', 'error');
          return;
        }

        const options = {
          key: rzpKey,
          amount: Math.round(cartTotal * 100), // amount in paise
          currency: 'INR',
          name: storeSettings?.storeName || 'M.L. Jewellers & Kitchenware',
          description: `Royal Jewellery Order (${cart.length} item${cart.length > 1 ? 's' : ''})`,
          image: storeSettings?.logoUrl || '',
          prefill: {
            name: formData.fullName,
            contact: formData.phone,
            email: formData.email
          },
          notes: {
            delivery_address: `${formData.address}, ${formData.city}, ${formData.state} - ${formData.pincode}`
          },
          theme: {
            color: '#d4af37'
          },
          handler: function (response) {
            finalizeOrder('Razorpay Online (UPI/Cards)', response.razorpay_payment_id);
          },
          modal: {
            ondismiss: function () {
              setIsProcessingRzp(false);
              addToast('Payment cancelled or checkout window closed.', 'info');
            }
          }
        };

        try {
          const rzp = new window.Razorpay(options);
          rzp.on('payment.failed', function (resp) {
            setIsProcessingRzp(false);
            addToast(`Payment failed: ${resp.error?.description || 'Gateway error'}`, 'error');
          });
          rzp.open();
        } catch (err) {
          console.error('Razorpay initialization error:', err);
          setIsProcessingRzp(false);
          addToast('Could not open Razorpay checkout modal: ' + err.message, 'error');
        }
      };

      if (!window.Razorpay) {
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.async = true;
        script.onload = launchRazorpay;
        script.onerror = () => {
          setIsProcessingRzp(false);
          addToast('Failed to load Razorpay checkout script.', 'error');
        };
        document.body.appendChild(script);
      } else {
        launchRazorpay();
      }
      return;
    }

    // 2. UPI QR CODE PAYMENT METHOD
    finalizeOrder('Showroom UPI QR Code', upiUtr ? `UTR-${upiUtr}` : '');
  };

  if (isSubmitted) {
    return (
      <div style={{ minHeight: '80vh', backgroundColor: '#07070b', color: '#fff', padding: '4rem 1rem' }}>
        <div style={{
          maxWidth: '650px',
          margin: '0 auto',
          backgroundColor: '#0c0c14',
          borderRadius: '20px',
          border: '1px solid var(--gold-400)',
          padding: '3rem 2rem',
          textAlign: 'center',
          boxShadow: '0 20px 50px rgba(0, 0, 0, 0.7)'
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            backgroundColor: 'rgba(52, 211, 153, 0.15)',
            border: '2px solid #34d399',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 1.5rem'
          }}>
            <Check size={44} color="#34d399" />
          </div>

          <span style={{
            display: 'inline-block',
            padding: '4px 12px',
            borderRadius: '999px',
            backgroundColor: 'rgba(212, 175, 55, 0.1)',
            color: 'var(--gold-400)',
            fontSize: '0.8rem',
            fontWeight: 700,
            letterSpacing: '1px',
            textTransform: 'uppercase',
            marginBottom: '0.75rem'
          }}>
            Royal Order Confirmed
          </span>

          <h1 style={{ fontSize: '2rem', fontWeight: 800, color: '#fff', marginBottom: '0.5rem' }}>
            Thank You, {formData.fullName}!
          </h1>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.95rem', maxWidth: '480px', margin: '0 auto 1.5rem' }}>
            Your order has been recorded in our vault registry. An insured armored transit dispatch order has been scheduled.
          </p>

          <div style={{
            backgroundColor: '#12121e',
            borderRadius: '12px',
            padding: '1.25rem',
            border: '1px solid rgba(212, 175, 55, 0.2)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '2rem',
            flexWrap: 'wrap',
            gap: '1rem'
          }}>
            <div style={{ textAlign: 'left' }}>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Order Docket Number</div>
              <div style={{ fontSize: '1.2rem', fontWeight: 800, color: 'var(--gold-400)' }}>{orderId}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Payment Verification</div>
              <div style={{ fontSize: '0.95rem', fontWeight: 600, color: paidPaymentId ? '#34d399' : '#fff' }}>
                {paidPaymentId ? `Razorpay: ${paidPaymentId}` : (paymentMethod === 'razorpay' ? 'Razorpay Online' : paymentMethod === 'upi' ? 'UPI / QR Verification' : paymentMethod === 'card' ? 'Credit/Debit Card' : 'Showroom Direct')}
              </div>
              {paidPaymentId && (
                <span style={{ fontSize: '0.68rem', color: '#10b981', fontWeight: 700, display: 'inline-block', marginTop: '2px' }}>
                  ✓ Secured & Verified via Razorpay
                </span>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/account" className="btn btn-gold" style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
              <span>📦 Track Order in Dashboard</span>
            </Link>
            <Link to="/" className="btn btn-secondary" style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
              Return to Storefront
            </Link>
            <a 
              href={`https://wa.me/${storeSettings?.supportWhatsapp || '919876543210'}?text=${encodeURIComponent(`Hello ML Jewellers, I have placed order ${orderId} for ${formatINR(cartTotal)}. Please confirm transit update.`)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-secondary"
              style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
            >
              Track on WhatsApp
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (cart.length === 0) {
    return (
      <div style={{ minHeight: '70vh', backgroundColor: '#07070b', color: '#fff', padding: '4rem 1rem', textAlign: 'center' }}>
        <div style={{ maxWidth: '500px', margin: '0 auto', backgroundColor: '#0c0c14', padding: '3rem 2rem', borderRadius: '16px', border: '1px solid var(--border-gold)' }}>
          <h2 style={{ fontSize: '1.4rem', fontWeight: 700, marginBottom: '0.5rem' }}>No Items to Checkout</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginBottom: '1.5rem' }}>
            Your shopping bag is empty. Please add jewellery before proceeding to checkout.
          </p>
          <Link to="/" className="btn btn-gold" style={{ textDecoration: 'none' }}>
            Explore Catalogue
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '80vh', backgroundColor: '#07070b', color: '#fff', padding: '2rem 1rem 5rem' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Breadcrumbs */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '1.75rem' }}>
          <Link to="/" style={{ color: 'var(--gold-400)', textDecoration: 'none' }}>Home</Link>
          <span>/</span>
          <Link to="/cart" style={{ color: 'var(--gold-400)', textDecoration: 'none' }}>Shopping Bag</Link>
          <span>/</span>
          <span style={{ color: '#fff' }}>Secure Checkout</span>
        </div>

        <div style={{ marginBottom: '2rem' }}>
          <h1 style={{ fontSize: '1.85rem', fontWeight: 800, color: '#fff', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <Lock size={26} color="var(--gold-400)" />
            Armored Checkout & VIP Delivery
          </h1>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginTop: '0.25rem' }}>
            Enter your insured dispatch destination and verify your bullion invoice.
          </p>
        </div>

        <form onSubmit={handlePlaceOrder}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem', alignItems: 'start' }}>
            {/* Left: Customer Info & Address */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', flex: '1 1 60%' }}>
              <div 
                className="checkout-box"
                style={{
                  backgroundColor: '#0c0c14',
                  borderRadius: '12px',
                  border: '1px solid var(--border-gold)',
                  padding: '1.25rem'
                }}
              >
                {!isCustomerLoggedIn && (
                  <div style={{
                    backgroundColor: 'rgba(40, 116, 240, 0.12)',
                    border: '1px solid rgba(40, 116, 240, 0.35)',
                    borderRadius: '6px',
                    padding: '10px 14px',
                    marginBottom: '1.2rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: '8px'
                  }}>
                    <span style={{ fontSize: '0.82rem', color: '#93c5fd' }}>
                      🔑 Already registered? Sign in with mobile & OTP for instant address autofill.
                    </span>
                    <button
                      type="button"
                      onClick={() => openCustomerAuthModal('login')}
                      style={{
                        backgroundColor: '#2874f0',
                        color: '#ffffff',
                        border: 'none',
                        padding: '4px 10px',
                        borderRadius: '3px',
                        fontSize: '0.78rem',
                        fontWeight: 700,
                        cursor: 'pointer'
                      }}
                    >
                      Login Now
                    </button>
                  </div>
                )}

                <h3 style={{ fontSize: '1.15rem', fontWeight: 700, color: '#fff', marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Truck size={18} color="var(--gold-400)" /> 1. Insured Delivery Address
                </h3>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div style={{ gridColumn: 'span 2' }}>
                    <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '0.4rem' }}>
                      Full Recipient Name *
                    </label>
                    <input 
                      type="text" 
                      name="fullName"
                      required
                      placeholder="e.g. Smt. Rajeshwari Sharma"
                      value={formData.fullName}
                      onChange={handleChange}
                      style={{
                        width: '100%',
                        backgroundColor: '#12121e',
                        border: '1px solid rgba(212, 175, 55, 0.3)',
                        borderRadius: '8px',
                        padding: '0.65rem 0.9rem',
                        color: '#fff',
                        fontSize: '0.9rem'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '0.4rem' }}>
                      Phone / Mobile Number *
                    </label>
                    <input 
                      type="tel" 
                      name="phone"
                      required
                      placeholder="e.g. 98765 43210"
                      value={formData.phone}
                      onChange={handleChange}
                      style={{
                        width: '100%',
                        backgroundColor: '#12121e',
                        border: '1px solid rgba(212, 175, 55, 0.3)',
                        borderRadius: '8px',
                        padding: '0.65rem 0.9rem',
                        color: '#fff',
                        fontSize: '0.9rem'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '0.4rem' }}>
                      Email Address (for Tax Invoice)
                    </label>
                    <input 
                      type="email" 
                      name="email"
                      placeholder="e.g. royal@jewellery.com"
                      value={formData.email}
                      onChange={handleChange}
                      style={{
                        width: '100%',
                        backgroundColor: '#12121e',
                        border: '1px solid rgba(212, 175, 55, 0.3)',
                        borderRadius: '8px',
                        padding: '0.65rem 0.9rem',
                        color: '#fff',
                        fontSize: '0.9rem'
                      }}
                    />
                  </div>

                  <div style={{ gridColumn: 'span 2' }}>
                    <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '0.4rem' }}>
                      Complete Postal Address / Landmark *
                    </label>
                    <input 
                      type="text" 
                      name="address"
                      required
                      placeholder="Apartment / Villa No., Street, Landmark"
                      value={formData.address}
                      onChange={handleChange}
                      style={{
                        width: '100%',
                        backgroundColor: '#12121e',
                        border: '1px solid rgba(212, 175, 55, 0.3)',
                        borderRadius: '8px',
                        padding: '0.65rem 0.9rem',
                        color: '#fff',
                        fontSize: '0.9rem'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '0.4rem' }}>
                      City / Town *
                    </label>
                    <input 
                      type="text" 
                      name="city"
                      required
                      placeholder="e.g. Mumbai / Jaipur"
                      value={formData.city}
                      onChange={handleChange}
                      style={{
                        width: '100%',
                        backgroundColor: '#12121e',
                        border: '1px solid rgba(212, 175, 55, 0.3)',
                        borderRadius: '8px',
                        padding: '0.65rem 0.9rem',
                        color: '#fff',
                        fontSize: '0.9rem'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '0.4rem' }}>
                      Postal PIN Code *
                    </label>
                    <input 
                      type="text" 
                      name="pincode"
                      required
                      placeholder="e.g. 400001"
                      value={formData.pincode}
                      onChange={handleChange}
                      style={{
                        width: '100%',
                        backgroundColor: '#12121e',
                        border: '1px solid rgba(212, 175, 55, 0.3)',
                        borderRadius: '8px',
                        padding: '0.65rem 0.9rem',
                        color: '#fff',
                        fontSize: '0.9rem'
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Payment Methods */}
              <div style={{
                backgroundColor: '#0c0c14',
                borderRadius: '16px',
                border: '1px solid var(--border-gold)',
                padding: '1.75rem'
              }}>
                <h3 style={{ fontSize: '1.15rem', fontWeight: 700, color: '#fff', marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <CreditCard size={18} color="var(--gold-400)" /> 2. Payment Guarantee Option
                </h3>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
                  {[
                    { 
                      id: 'razorpay', 
                      name: 'Razorpay Instant Gateway (UPI, Cards, NetBanking, Wallets)', 
                      desc: '⚡ 100% Instant & Secure. Pay via Google Pay, PhonePe, Paytm, BHIM UPI, Visa, RuPay, Mastercard or 50+ Banks.',
                      badge: 'RECOMMENDED' 
                    },
                    { 
                      id: 'upi', 
                      name: 'Direct Showroom UPI / QR Code Verification', 
                      desc: 'Scan showroom QR code with any UPI app and confirm order instantly.',
                      badge: 'SCAN & PAY'
                    }
                  ].map((pm) => (
                    <label 
                      key={pm.id}
                      onClick={() => setPaymentMethod(pm.id)}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '0.75rem',
                        padding: '1rem',
                        borderRadius: '10px',
                        backgroundColor: paymentMethod === pm.id ? 'rgba(212, 175, 55, 0.12)' : '#12121e',
                        border: paymentMethod === pm.id ? '2px solid var(--gold-400)' : '1px solid rgba(255, 255, 255, 0.08)',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        position: 'relative'
                      }}
                    >
                      <input 
                        type="radio" 
                        name="paymentMethod" 
                        checked={paymentMethod === pm.id} 
                        onChange={() => setPaymentMethod(pm.id)}
                        style={{ marginTop: '0.2rem', accentColor: 'var(--gold-400)' }}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                          <span style={{ fontWeight: 700, color: '#fff', fontSize: '0.92rem' }}>{pm.name}</span>
                          {pm.badge && (
                            <span style={{
                              background: pm.id === 'razorpay' ? 'linear-gradient(135deg, #d4af37, #f59e0b)' : '#10b981',
                              color: pm.id === 'razorpay' ? '#0a0a0f' : '#ffffff',
                              fontSize: '0.62rem',
                              fontWeight: 900,
                              padding: '0.15rem 0.5rem',
                              borderRadius: '999px',
                              letterSpacing: '0.05em'
                            }}>
                              {pm.badge}
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>{pm.desc}</div>
                      </div>
                    </label>
                  ))}

                  {/* UPI QR Code Display Card */}
                  {paymentMethod === 'upi' && (
                    <div style={{
                      marginTop: '0.5rem',
                      padding: '1.25rem',
                      background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.08) 0%, rgba(13, 13, 22, 0.95) 100%)',
                      border: '1px solid var(--border-gold)',
                      borderRadius: '12px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '0.9rem',
                      textAlign: 'center'
                    }}>
                      <div style={{
                        fontSize: '0.9rem',
                        fontWeight: 700,
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        <QrCode size={20} color="var(--gold-400)" />
                        <span>Scan Showroom QR Code to Complete Payment</span>
                      </div>

                      {/* Scannable Dynamic QR Code Image */}
                      <div style={{
                        background: '#ffffff',
                        padding: '12px',
                        borderRadius: '10px',
                        border: '2px solid var(--gold-400)',
                        boxShadow: '0 4px 18px rgba(212, 175, 55, 0.3)',
                        display: 'inline-block'
                      }}>
                        <img
                          src={storeSettings?.upiQrImage || `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(`upi://pay?pa=${storeSettings?.upiId || 'mljewellers@icici'}&pn=${encodeURIComponent(storeSettings?.storeName || 'ML Jewellers')}&am=${cartTotal}&cu=INR&tn=Order`)}`}
                          alt="Showroom UPI QR Code"
                          style={{ width: '180px', height: '180px', display: 'block' }}
                        />
                      </div>

                      <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                        <div>Payable Amount: <strong style={{ color: 'var(--gold-400)', fontSize: '1.05rem' }}>{formatINR(cartTotal)}</strong></div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginTop: '6px', flexWrap: 'wrap' }}>
                          <span>Official UPI ID: <strong style={{ color: '#fff' }}>{storeSettings?.upiId || 'mljewellers@icici'}</strong></span>
                          <button
                            type="button"
                            onClick={copyUpiId}
                            style={{
                              background: 'rgba(212, 175, 55, 0.15)',
                              border: '1px solid var(--border-gold)',
                              color: 'var(--gold-400)',
                              padding: '2px 8px',
                              borderRadius: '4px',
                              fontSize: '0.74rem',
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                          >
                            {copiedUpi ? <Check size={12} /> : <Copy size={12} />}
                            <span>{copiedUpi ? 'Copied' : 'Copy'}</span>
                          </button>
                        </div>
                      </div>

                      <div style={{ width: '100%', maxWidth: '420px', textAlign: 'left', marginTop: '4px' }}>
                        <label style={{ display: 'block', fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '0.35rem' }}>
                          UPI Transaction Ref / UTR Number (Optional / For Instant Dispatch)
                        </label>
                        <input
                          type="text"
                          placeholder="e.g. 4289XXXXXXXX (12-digit UTR)"
                          value={upiUtr}
                          onChange={(e) => setUpiUtr(e.target.value)}
                          style={{
                            width: '100%',
                            backgroundColor: '#12121e',
                            border: '1px solid rgba(212, 175, 55, 0.3)',
                            borderRadius: '8px',
                            padding: '0.6rem 0.85rem',
                            color: '#fff',
                            fontSize: '0.85rem'
                          }}
                        />
                      </div>

                      <div style={{ fontSize: '0.75rem', color: '#10b981', display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <CheckCircle size={14} />
                        <span>Instant scan via Google Pay, PhonePe, Paytm, BHIM UPI & Banking Apps</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right: Summary */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              <div style={{
                backgroundColor: '#0c0c14',
                borderRadius: '16px',
                border: '1px solid var(--border-gold)',
                padding: '1.5rem'
              }}>
                <h3 style={{ fontSize: '1.15rem', fontWeight: 700, color: '#fff', marginBottom: '1.25rem' }}>
                  Summary ({cart.length} Items)
                </h3>

                <div style={{ maxHeight: '200px', overflowY: 'auto', marginBottom: '1.25rem' }}>
                  {cart.map((item, i) => {
                    const prod = item?.product || item;
                    const pricing = calculateProductPrice ? calculateProductPrice(prod, masterMetalRates) : null;
                    const unitPrice = pricing?.finalPrice || Number(prod.price) || 0;
                    return (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', padding: '0.4rem 0', borderBottom: '1px solid rgba(255, 255, 255, 0.06)' }}>
                        <span style={{ color: '#fff', maxWidth: '65%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {item.quantity || 1}x {prod.name}
                        </span>
                        <span style={{ color: 'var(--gold-400)', fontWeight: 600 }}>
                          {formatINR(unitPrice * (item.quantity || 1))}
                        </span>
                      </div>
                    );
                  })}
                </div>

                {/* Interactive Coupon Code Form */}
                <div style={{
                  marginBottom: '1rem',
                  padding: '0.9rem',
                  background: 'rgba(255, 255, 255, 0.03)',
                  border: '1px solid rgba(212, 175, 55, 0.25)',
                  borderRadius: '10px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '0.5rem', fontSize: '0.8rem', fontWeight: 700, color: '#fff' }}>
                    <Tag size={15} color="var(--gold-400)" />
                    <span>Have a Privilege Coupon?</span>
                  </div>

                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      type="text"
                      placeholder="Enter Coupon (e.g. ROYAL10)"
                      value={inputCoupon}
                      onChange={(e) => setInputCoupon(e.target.value.toUpperCase())}
                      style={{
                        flex: 1,
                        background: '#12121e',
                        border: '1px solid rgba(255, 255, 255, 0.15)',
                        borderRadius: '6px',
                        padding: '0.55rem 0.8rem',
                        color: '#fff',
                        fontSize: '0.84rem',
                        textTransform: 'uppercase',
                        outline: 'none'
                      }}
                    />
                    <button
                      type="button"
                      onClick={handleApplyCoupon}
                      className="btn btn-gold btn-sm"
                      style={{ padding: '0.55rem 1rem', fontSize: '0.8rem', whiteSpace: 'nowrap' }}
                    >
                      Apply
                    </button>
                  </div>

                  {/* Coupon Applied Tag */}
                  {couponCode && (
                    <div style={{
                      marginTop: '0.65rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '0.45rem 0.75rem',
                      background: 'rgba(16, 185, 129, 0.12)',
                      border: '1px solid rgba(16, 185, 129, 0.3)',
                      borderRadius: '6px',
                      fontSize: '0.78rem',
                      color: '#34d399'
                    }}>
                      <span>✓ <strong>{couponCode}</strong> Applied (-{appliedDiscountPercent}%)</span>
                      <button
                        type="button"
                        onClick={removeCoupon}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#fb7185',
                          cursor: 'pointer',
                          fontWeight: 700,
                          fontSize: '0.75rem'
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  )}

                  {couponMessage && !couponCode && (
                    <div style={{
                      marginTop: '0.45rem',
                      fontSize: '0.75rem',
                      color: '#fb7185'
                    }}>
                      {couponMessage?.text || couponMessage}
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', fontSize: '0.85rem', borderTop: '1px solid rgba(255, 255, 255, 0.08)', paddingTop: '0.8rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--text-muted)' }}>
                    <span>Bullion Subtotal</span>
                    <span style={{ color: '#fff' }}>{formatINR(cartSubtotal)}</span>
                  </div>

                  {discountAmount > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', color: '#34d399' }}>
                      <span>Savings Applied</span>
                      <span>-{formatINR(discountAmount)}</span>
                    </div>
                  )}

                  <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--text-muted)' }}>
                    <span>GST (3% Bullion)</span>
                    <span style={{ color: '#fff' }}>{formatINR(gstAmount)}</span>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--text-muted)' }}>
                    <span>Insured Transit</span>
                    <span style={{ color: '#34d399' }}>{shippingFee === 0 ? 'FREE' : formatINR(shippingFee)}</span>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1.25rem', fontWeight: 800, color: '#fff', paddingTop: '0.75rem', borderTop: '1px solid var(--border-gold)' }}>
                    <span>Payable Total</span>
                    <span style={{ color: 'var(--gold-400)' }}>{formatINR(cartTotal)}</span>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isProcessingRzp}
                  className="btn btn-gold"
                  style={{
                    width: '100%',
                    marginTop: '1.5rem',
                    padding: '0.95rem',
                    fontSize: '1rem',
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    opacity: isProcessingRzp ? 0.7 : 1,
                    cursor: isProcessingRzp ? 'wait' : 'pointer'
                  }}
                >
                  {isProcessingRzp ? (
                    <>
                      <Sparkles size={16} /> Opening Razorpay Secure Modal...
                    </>
                  ) : paymentMethod === 'razorpay' ? (
                    <>
                      <Lock size={16} /> Pay {formatINR(cartTotal)} via Razorpay & Confirm
                    </>
                  ) : (
                    <>
                      <Lock size={16} /> Confirm & Place Order ({formatINR(cartTotal)})
                    </>
                  )}
                </button>

                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textAlign: 'center', marginTop: '0.75rem' }}>
                  ✓ 256-Bit Bank Encryption • Powered by Razorpay • 100% Insured Delivery
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
